import emojis from "./emoji.json";
import markdownit from "markdown-it";
import taskLists from "markdown-it-task-lists";
import markdownSub from "markdown-it-sub";
import markdownSup from "markdown-it-sup";
import DOMPurify from "dompurify";
import { parseBlocks, parseIncompleteMarkdown } from "streamdown-vue";
import { formatTime } from "./dates";

export function extractUniqueFilters(filters) {
  const result = {};
  const prefixes = new Set();

  for (const [key, value] of Object.entries(filters)) {
    const match = key.match(/^(.*)_(start_date|end_date)$/);
    if (match) {
      const prefix = match[1];
      const suffix = match[2];
      if (suffix === "start_date" && !prefixes.has(prefix)) {
        result[key] = value;
        prefixes.add(prefix);
      }
    } else {
      result[key] = value;
    }
  }

  return result;
}

export function excludeEmptyValues(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        value !== "" &&
        (!Array.isArray(value) || value.length > 0) // Keep arrays only if they are non-empty
    )
  );
}

export function excludeEmptyValuesAndArrays(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) =>
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
    )
  );
}

export function objectToQueryString(params) {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach((val) => searchParams.append(key, val));
    } else {
      searchParams.set(key, value);
    }
  }

  return searchParams.toString();
}

export function createDictionaryFromDropdownOptions(
  options,
  textField = "text"
) {
  return options.reduce((acc, option) => {
    acc[option.value] = option[textField];
    return acc;
  }, {});
}

export function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== "object" ||
    typeof obj2 !== "object" ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export function arraysEqual(arr1 = [], arr2 = []) {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;

  for (let i = 0; i < arr1.length; i++) {
    if (!deepEqual(arr1[i], arr2[i])) return false;
  }

  return true;
}

export function objectsEqual(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (
    obj1 === null ||
    obj2 === null ||
    typeof obj1 !== "object" ||
    typeof obj2 !== "object"
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !objectsEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export function getInitials(name) {
  if (name) {
    return name
      .replace(/^\(Disabled\)\s*/, "") // Remove "(Disabled)" prefix if it exists
      .split(" ")
      .map((word) => (word[0] ? word[0].toUpperCase() : ""))
      .join("")
      .slice(0, 2);
  }
  return "N/A";
}

export function formatUsername(username) {
  if (!username) return "";
  if (username === "N/A") return username;
  return username.includes("@")
    ? `@${getInitials(username)}`
    : getInitials(username);
}

export function getUserNameAndFallbacksFromRawData(rawUserData) {
  const full_name =
    rawUserData?.first_name && rawUserData?.last_name
      ? `${rawUserData.first_name} ${rawUserData.last_name}`
      : "";

  const is_disabled = rawUserData?._is_disabled;
  const result =
    full_name || rawUserData?.username || rawUserData?.email || "Unnamed User";

  return is_disabled ? `(Disabled) ${result}` : result;
}

export function toPascalCase(str) {
  const isPascalCase = /^[A-Z][a-zA-Z]*$/.test(str);

  if (isPascalCase) {
    return str;
  }

  return str
    .replace(/[^a-zA-Z0-9]+/g, " ") // Replace non-alphanumeric characters with spaces
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
}

export function debounce(fn, wait = 500) {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
    }, wait);
  };
}

export function truncateString(str, length, defaultValue = "") {
  if (!str) return defaultValue;
  if (str.length >= length) {
    return `${str.slice(0, length)}...`;
  }
  return str;
}

export function formatCount(count) {
  if (count === null || count === undefined) return null;
  return count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count;
}

export function calculatePercent(target, total) {
  if (
    target === undefined ||
    target === null ||
    total === undefined ||
    total === null
  ) {
    return 0;
  }
  if (total === 0) return 0;
  return Math.floor((target / total) * 100);
}

export function createEntitiesDictionary(array) {
  if (!Array.isArray(array)) return;
  return array.reduce((dict, entity) => {
    const idKey = entity.id ? "id" : "_id";
    dict[entity[idKey]] = entity;
    return dict;
  }, {});
}

export function initializeMultipleDropdownOptions(queryParam) {
  if (typeof queryParam === "string") {
    return [queryParam];
  }
  return Array.isArray(queryParam) ? queryParam : [];
}

export function capitalizeFirstLetter(str) {
  if (typeof str !== "string" || str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const unwrapProxies = (obj) => {
  const result = {};

  for (const key in obj) {
    const value = obj[key];
    result[key] = isProxy(value) ? toRaw(value) : value;
  }
  return result;
};

export function adaptToHtml(text) {
  if (!text || text == "") {
    return text;
  }
  let str = removeLastChar(text, "\n");
  str = replaceAll(str, "\\n", "<br>");
  str = replaceAll(str, "\n", "<br>");
  str = replaceAll(str, "\\", "");
  return str;
}

export function stripWhitespaceCharacters(text) {
  if (!text || text == "") return text;

  let str = removeLastChar(text, "\n");
  str = replaceAll(str, "\\n", "");
  str = replaceAll(str, "\n", "");
  str = replaceAll(str, "\\t", "");
  str = replaceAll(str, "\t", "");
  str = replaceAll(str, "\\r", "");
  str = replaceAll(str, "\t", "");

  return str
}

// do not use, garbage
export function urlifyOnly(text) {
  if (!text || text == "") return text;
  let urlRegex = /(?<!href=["'])(https?:\/\/[^\s]+)/g;
  let str = text.replace(urlRegex, (url) => {
    if (/\p{Extended_Pictographic}/u.test(url)) {
      url = unemojify(url).toString();
    }
    return '<a href="' + url + '" target="_blank">' + url + "</a>";
  });
  return str;
}

// do not use, garbage
export function urlify(text) {
  if (!text || text == "") {
    return text;
  }
  let urlRegex = /(?<!href=["'])(https?:\/\/[^\s]+)/g;
  let str = text.replace(urlRegex, (url) => {
    if (/\p{Extended_Pictographic}/u.test(url)) {
      url = unemojify(url).toString();
    }
    return '<a href="' + url + '" target="_blank">' + url + "</a>";
  });
  str = removeLastChar(str, "\n");
  str = replaceAll(str, "\\n", "<br>");
  str = replaceAll(str, "\n", "<br>");
  return str;
}

export function isValidUrl(url) {
  const pattern =
    /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
  return url && pattern.test(url);
}

export function unemojify(text) {
  let _emojis = emojis;
  const emoji = Object.values(_emojis).filter((e) => {
    return text.includes(e);
  });
  if (emoji && emoji.length > 0) {
    let emoji_code = null;
    for (var key in _emojis) {
      if (_emojis[key] === emoji[0]) {
        emoji_code = key;
      }
    }
    text = text.replace(`${emoji[0]}`, `:${emoji_code}`);
    if (/\p{Extended_Pictographic}/u.test(text)) {
      text = unemojify(text);
    }
    return text;
  } else return text;
}

function replaceAll(str, find, replace) {
  return str.replace(new RegExp(escapeRegExp(find), "g"), replace);
}

function removeLastChar(text, char) {
  let res = text.charAt(text.length - 1);
  if (res === char) {
    return removeLastChar(text.substring(0, text.length - 1), char);
  }
  return text;
}

function escapeRegExp(find) {
  return find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

export function filterListByOrgId(list, value, key = "organization_id") {
  return list.filter((item) => item[key] === value);
}

function listToText(element) {
  const listItems = Array.from(element.children).filter(
    (child) => child.localName === "li"
  );

  const extractIndentLevel = (li) => {
    const indentClass = Array.from(li.classList).find((cls) =>
      cls.startsWith("ql-indent-")
    );
    if (indentClass === undefined) return 0;

    const parts = indentClass.split("-");
    if (parts.length !== 3) return 0;

    const indent = parseInt(parts[2]);
    if (isNaN(indent)) return 0;
    return indent;
  };

  const buildIndentText = (indexLevel) => {
    const spaces = [];
    for (let i = indexLevel * 4; i > 0; i--) {
      spaces.push("&nbsp;");
    }
    return spaces.join("");
  };

  const items = [];
  for (const li of listItems) {
    const indentLevel = extractIndentLevel(li);
    console.log(indentLevel, li);
    const indentText = buildIndentText(indentLevel);
    items.push(`${indentText}-&nbsp;${li.textContent}`);
  }

  return items.join("<br>");
}

export function formatEditorHtml(editorText) {
  return editorText;
  /*
  console.log(editorText);
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = editorText;

  const brRegex = /^(<br>)*$/;

  const contentList = Array.from(tempDiv.children).filter(
    (child) => child.localName === "p" || child.localName === "ol"
  );
  const reducedContentList = contentList.reduce((acc, element) => {
    switch (element.localName) {
      case "p":
        const item = element.innerHTML;
        if (item == "<br>") {
          if (acc.length > 0 && brRegex.test(acc[acc.length - 1])) {
            acc[acc.length - 1] += item;
          } else {
            acc.push("");
          }
        } else {
          acc.push(item);
        }
      case "ol":
        acc.push(listToText(element));
        break;
    }

    return acc;
  }, []);

  return "<p>" + reducedContentList.join("<br>") + "</p>";
  */
}

export const cleanWysiwygHtml = (html) => {
  // only remove newlines characters
  let cleaned = html.replaceAll(/\n/g, "");
  cleaned = cleaned.replaceAll(/\s*?white-space: pre.*?;/g, "");
  return cleaned;
}

// Use a dom parser so we dont transform already transformed links in <a> tags again
export const processLinksHTMLText = (html) => {
  return html;
  /*
  // regex to match all 'http://' or 'https://' or 'www.'
  const urlRegex = /(\b(?:https?:\/\/|www\.)[^\s<]+)/gi;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const traverse = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.nodeValue;

        const replaced = text.replace(urlRegex, (match) => {
          let url = match.trim();
          // prepend 'https://' if its not http or https already
          if (!/^https?:\/\//i.test(url)) {
            url = "https://" + url;
          }
          return `<a href="${url}" target="_blank" rel="noopener noreferrer">${match}</a>`;
        });

        if (replaced !== text) {
          const temp = document.createElement("div");
          temp.innerHTML = replaced;
          while (temp.firstChild) {
            node.insertBefore(temp.firstChild, child);
          }
          node.removeChild(child);
        }
      } else if (
        child.nodeType === Node.ELEMENT_NODE &&
        child.nodeName.toLowerCase() !== "a"
      ) {
        traverse(child);
      }
    });
  };

  traverse(doc.body);
  return doc.body.innerHTML;
  */
};

export function isStructEmpty(obj) {
  return Object.keys(obj).length === 0;
}

export function generateUniqueId() {
  // Generate a unique ID using current timestamp and random numbers
  return "ws-" + Date.now() + "-" + Math.floor(Math.random() * 1000);
}

export function generateSafeFilename(originalName) {
  const extension = originalName.split(".").pop().toLowerCase();
  const baseName = originalName
    .replace(/\.[^/.]+$/, "") // remove extension
    .replace(/[^a-z0-9_-]/gi, "-") // replace unsafe chars
    .toLowerCase()
    .slice(0, 50); // limit length if needed

  const uniqueSuffix = crypto.randomUUID(); // or Date.now()
  return `${baseName}-${uniqueSuffix}.${extension}`;
}

// Wrapping table/image in div is for horizontal scrolling purposes.
const mdWrapTableInDiv = (md) => {
  md.renderer.rules.table_open = () => '<div class="table-wrapper"><table>';
  md.renderer.rules.table_close = () => '</table></div>';
}

const mdWrapImgInDiv = (md) => {
  const originalRenderer = md.renderer.rules.image;
  md.renderer.rules.image = (...args) => `<div class="img-wrapper">${originalRenderer(...args)}</div>`;
}

// !!! IMPORTANT !!!
// this converter will automatically wrap bare HTTP URLs with <a></a> tags!
const md = markdownit({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true
})
  .use(taskLists, { enabled: true })
  .use(markdownSub, { enabled: true })
  .use(markdownSup, { enabled: true })
  .use(mdWrapTableInDiv, { enabled: true })
  .use(mdWrapImgInDiv, { enabled: true });

// Converts commonmark + gfm to html
// Pass in options if we want to transform elements or add classes
export function commonMarkToHtml(commonmark, options = {linksNewTab: true}) {
  if (!commonmark) return '<div class="markdown"></div>';
  console.debug("commonmark-request: ", commonmark)
  
  // md -> html
  const html = md.render(commonmark)
  console.debug("html: ", html)

  // Check options
  DOMPurify.removeAllHooks?.();

  // Run this after the attributes have been sanitized
  // TODO: Think about a way to do this generally. We do not want the same problem as the 
  // get methods on the BE where we have to make a check for every single filter param
  DOMPurify.addHook('afterSanitizeAttributes', (node)=>{
    if(options.linksNewTab && node.tagName === 'A') {
      node.setAttribute('target', '_blank')
    }

  })

  // validate html
  const santized = DOMPurify.sanitize(html,
     { 
      USE_PROFILES: {html: true},
      ADD_TAGS: ["s", "sup", "sub"],
      ADD_ATTR: ['class','target','rel'] // Need this for later, so applied styles stay
  })
  console.debug("sanitized: ", santized)

  return `<div class="markdown">${santized}</div>`
}

// Transforms the citations in the html response to tooltips 
export function processCitations(html, citations) {
  let citationIdx = 1;
  // citations is an array
  // we need it in an object form where, it has its array index as a prop
  const citationsDict = citations.reduce((dict, citation)=>{
    dict[citation.anchor] = {
      ...citation,
      index: citationIdx,
    }
    citationIdx++

    return dict
  }, {}) 

  const messageWithCitations = html.replace(/\[\[cite:(\d+)\]\]/g, (match, numStr) => {
    // get the citation
    const citation = citationsDict[match]
    if(!citation) return ""

    const num = citation.index || 'N/A'
    const emoji = citation.page ? '📄' : citation.timestamp ? '🎬' : '🌐'
    // Assume there is only ever a page or a timestamp, not both
    const title = [
      `${emoji} ${truncateString(citation.name, 40)}`,
      citation.page && `on page(s) ${citation.page}`,
      !citation.page && citation.timestamp && `at ${formatTime(citation.timestamp)}`
    ].filter(Boolean).join(' ');
    const el = `
      <a
        ${citation.url ? `href="${citation.url}"` : ''}
        class="citation"
        target="_blank"
        data-bs-toggle="tooltip"
        data-bs-placement="auto"
        data-bs-html="true"
        title="${title}"
        onclick="event.stopPropagation()"
      >
        [${num}]
      </a>
    `
    return el
  })
  return messageWithCitations
}

export function purifyHtml(html) {
  const santized = DOMPurify.sanitize(html,
     { 
      USE_PROFILES: {html: true},
      ADD_TAGS: ["s", "sup", "sub"],
      ADD_ATTR: ['class','target','rel'] // Need this for later, so applied styles stay
  })
  // console.debug("sanitized: ", santized)
  DOMPurify.removeAllHooks();

  return `<div class="purified">${santized}</div>`
}

export function repairMarkdown(text) {
  const repaired = parseIncompleteMarkdown(text);
  const blocks = parseBlocks(repaired);
  return blocks.join("");
}

// do not use, garbage
export function cleanHtml(text) {
  return stripWhitespaceCharacters(urlifyOnly(purifyHtml(text)));
};


export function isProd(domain = 'ezeeassist.ai') {
  return window.location.hostname === domain ? true : false
}

/** Coerce query param which might be string[] to string by assuming last element is desired. */
export function queryParamToString(param, def = "") {
  return Array.isArray(param) ? param.at(-1) : param ?? def;
}

/** Coerce query param which might be string to string[], using empty array as fallback. */
export function queryParamToArray(param, def = undefined) {
  if (param === undefined) return def ?? [];
  return Array.isArray(param) ? param : [param];
}

// Credits to https://stackoverflow.com/a/52171480
export const hashString = (s) => {
  for(var i=0,h=9;i<s.length;)
    h=Math.imul(h^s.charCodeAt(i++),9**9);
  return (h^h>>>9).toString(36);
};
